import { useNotification } from "@context/NotificationContext";
import type { VesselCallError } from "@features/vessel-call/vessel-call-status/VesselCallStatus.types";
import type { ResumeInterruptionVesselCallByIdArgs } from "@services/vesselCall/types";
import { resumeInterruptionVesselCallById } from "@services/vesselCallApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";

const LANG_BASE = "vessel-call.resume";

export const useResumeVesselCallById = ({
  handleSuccess,
  handleError,
}: {
  handleSuccess: (status?: string) => void;
  handleError?: (error: VesselCallError) => void;
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();

  const resumeInterruptionMutation = useMutation({
    mutationFn: (variables: Parameters<typeof resumeInterruptionVesselCallById>[0]) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "ResumeVesselCall",
          action: "resume_vessel_call",
          entityType: "vessel_call",
        },
        (transactionId) =>
          resumeInterruptionVesselCallById({
            ...variables,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async (_data) => {
      const status = "RESUME_PENDING";
      handleSuccess(status);
      notify({
        title: t(`${LANG_BASE}.request-submitted-success-title`),
        type: "success",
      });
    },
    onError: (err: any) => {
      notify({
        title: t(`${LANG_BASE}.request-submitted-error-title`),
        description: err?.response?.data?.message ?? err?.response?.data?.detail,
        type: "error",
      });
      handleError?.(err);
    },
  });

  const resumeInterruption = async ({
    service,
    vesselCallId,
    params,
  }: {
    service: string;
    vesselCallId: string;
    params: ResumeInterruptionVesselCallByIdArgs;
  }) => {
    await resumeInterruptionMutation.mutateAsync({
      service,
      vesselCallId,
      params,
    });
  };

  return {
    resumeInterruption,
  };
};
